import type { GetResource } from "@flexcompute/uvf";
import { simpleGetResourceFactory } from "@flexcompute/uvf";

/**
 * Module-level ArrayBuffer cache keyed by canonical resource path
 * (not the pre-signed URL, which expires). Survives across entity
 * switches so re-visiting an entity is near-instant.
 *
 * Capped at MAX_CACHE_BYTES to prevent unbounded memory growth
 * (large meshes can be 50-100MB each). Evicts oldest entries first.
 */
const MAX_CACHE_BYTES = 200 * 1024 * 1024; // 200 MB
const bufferCache = new Map<string, ArrayBuffer>();
const insertionOrder: string[] = [];
let totalBytes = 0;

function evictUntilFits(newBytes: number): void {
  while (totalBytes + newBytes > MAX_CACHE_BYTES && insertionOrder.length > 0) {
    const oldest = insertionOrder.shift()!;
    const buf = bufferCache.get(oldest);
    if (buf) {
      totalBytes -= buf.byteLength;
      bufferCache.delete(oldest);
    }
  }
}

/**
 * Create a UVF GetResource backed by a mutable URL map ref.
 *
 * The manifest references files by relative path (e.g. "geometry_0.bin").
 * We resolve each path against the current `urlMapRef.current` at call
 * time, so the same loader works across entity switches.
 *
 * Layers an ArrayBuffer cache on top of `simpleGetResourceFactory`:
 * first load fetches over the network; subsequent loads for the same
 * canonical path return a cloned cached buffer instantly.
 */
export function createPresignedResourceLoader(
  urlMapRef: { current: Record<string, string> } | Record<string, string>,
): GetResource<null> {
  const resolveMap = (): Record<string, string> =>
    "current" in urlMapRef
      ? (urlMapRef as { current: Record<string, string> }).current
      : (urlMapRef as Record<string, string>);

  const getResourceUrl = async (path: string): Promise<string> => {
    const map = resolveMap();
    if (map[path]) return map[path];
    const basename = path.split("/").pop() ?? path;
    if (map[basename]) return map[basename];
    return path;
  };

  const inner = simpleGetResourceFactory(getResourceUrl);

  const cachedGetResource: GetResource<null> = async (path, options) => {
    // Key by the resolved URL's pathname (stable across signatures)
    // so different entities with the same file names don't collide.
    const resolvedUrl = await getResourceUrl(path);
    let cacheKey: string;
    try {
      cacheKey = new URL(resolvedUrl).pathname;
    } catch {
      cacheKey = resolvedUrl;
    }

    const cached = bufferCache.get(cacheKey);
    if (cached) return cached.slice(0);

    const result = await inner(path, options);
    const clone = result.slice(0);
    // Guard against concurrent fetches for the same key: if another
    // request resolved first, skip insertion to avoid double-counting.
    if (!bufferCache.has(cacheKey)) {
      evictUntilFits(clone.byteLength);
      bufferCache.set(cacheKey, clone);
      insertionOrder.push(cacheKey);
      totalBytes += clone.byteLength;
    }
    return result;
  };

  return cachedGetResource;
}

/** Clear the binary buffer cache (useful on logout or project switch). */
export function clearResourceCache(): void {
  bufferCache.clear();
  insertionOrder.length = 0;
  totalBytes = 0;
}
